import { useTrail, animated } from "@react-spring/web";
import { cn } from "@/lib/utils";
import type { ImpactBucket } from "@/services/salesforce-release";

const BUCKETS: ImpactBucket[] = ["Breaking", "Deprecated", "Review", "Improvement", "OK"];

const SEGMENT_COLOR: Record<ImpactBucket, string> = {
  Breaking: "bg-red-500",
  Deprecated: "bg-orange-500",
  Review: "bg-yellow-500",
  Improvement: "bg-emerald-500",
  OK: "bg-slate-400",
};

const PILL_ACTIVE_TEXT: Record<ImpactBucket, string> = {
  Breaking: "text-red-600",
  Deprecated: "text-orange-600",
  Review: "text-yellow-600",
  Improvement: "text-emerald-600",
  OK: "text-slate-500",
};

interface Props {
  /** dashboard.severity_heatmap: { component_type: { Breaking: n, Deprecated: n, ... } } */
  matrix: Record<string, Record<string, number>>;
  /** Segment click → bucket + type both filtered. */
  onCellClick?: (bucket: ImpactBucket, componentType: string) => void;
  /** Row-label click → "all buckets" for this metadata type. */
  onRowClick?: (componentType: string) => void;
  /** Bucket pill click → "all types" for this bucket. */
  onColumnClick?: (bucket: ImpactBucket) => void;
  /** Reset click → no filter, show all. */
  onAllClick?: () => void;
  activeBucket?: string;
  activeType?: string;
  /** When set, only these metadata types render — missing ones as zero rows. */
  visibleTypes?: string[];
}

/** Lexicographic compare on [Breaking, Deprecated, Review, Improvement, OK] counts,
 *  descending — a type with more Breaking always outranks one with more OK, no
 *  matter the raw totals. Ties fall through to the next-most-severe bucket. */
function severityRank(row: Record<string, number> | undefined): number[] {
  return BUCKETS.map((b) => row?.[b] ?? 0);
}

function compareSeverity(a: number[], b: number[]): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return b[i] - a[i];
  }
  return 0;
}

export function ImpactSeverityRanked({
  matrix, onCellClick, onRowClick, onColumnClick, onAllClick, activeBucket, activeType, visibleTypes,
}: Props) {
  const rowTotal = (t: string) => Object.values(matrix[t] ?? {}).reduce((x, y) => x + y, 0);

  const allTypes = visibleTypes && visibleTypes.length > 0 ? visibleTypes : Object.keys(matrix);
  const types = [...allTypes].sort((ta, tb) =>
    compareSeverity(severityRank(matrix[ta]), severityRank(matrix[tb])),
  );

  const colTotal: Record<ImpactBucket, number> = { Breaking: 0, Deprecated: 0, Review: 0, Improvement: 0, OK: 0 };
  for (const t of types) {
    for (const b of BUCKETS) colTotal[b] += matrix[t]?.[b] ?? 0;
  }
  const grandTotal = BUCKETS.reduce((s, b) => s + colTotal[b], 0);
  const maxRowTotal = types.reduce((m, t) => Math.max(m, rowTotal(t)), 0);

  const rowTrail = useTrail(types.length, {
    from: { opacity: 0, transform: "translateY(4px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 280, friction: 34, clamp: true },
  });

  if (types.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card px-5 py-4">
        <h3 className="text-sm font-semibold">Severity breakdown</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          No findings yet — this will populate after the next scan.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Severity breakdown</h3>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            Ranked worst-first. Click a bar segment, row, or bucket to drill into the findings table below.
          </p>
        </div>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {types.length} component types
        </span>
      </div>

      {/* Bucket totals — the "first glance" summary + column-wide filter */}
      <div className="flex items-center gap-1.5 flex-wrap border-b border-border px-5 py-2.5">
        {BUCKETS.map((b) => (
          <button
            key={b}
            type="button"
            disabled={colTotal[b] === 0}
            onClick={() => colTotal[b] > 0 && onColumnClick?.(b)}
            className={cn(
              "flex items-center gap-1.5 rounded-full border border-border/70 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted",
              colTotal[b] === 0 && "opacity-40 cursor-default hover:bg-transparent",
              activeBucket === b && activeType === "all" && "ring-1 ring-foreground bg-muted",
            )}
            title={`${b}: ${colTotal[b]} finding${colTotal[b] === 1 ? "" : "s"} across all types`}
          >
            <span className={cn("h-2 w-2 rounded-full", SEGMENT_COLOR[b])} />
            <span className={PILL_ACTIVE_TEXT[b]}>{b}</span>
            <span className="font-mono text-muted-foreground">{colTotal[b]}</span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => onAllClick?.()}
          className={cn(
            "ml-auto rounded-full border border-border/70 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-muted",
            activeBucket === "all" && activeType === "all" && "ring-1 ring-foreground bg-muted",
          )}
          title={`View all ${grandTotal} findings (no filter)`}
        >
          All · {grandTotal}
        </button>
      </div>

      <div className="px-5 py-4 space-y-1.5">
        {rowTrail.map((style, i) => {
          const t = types[i];
          const total = rowTotal(t);
          const barPct = maxRowTotal > 0 ? Math.max((total / maxRowTotal) * 100, total > 0 ? 3 : 0) : 0;
          const segments = BUCKETS
            .map((b) => ({ bucket: b, count: matrix[t]?.[b] ?? 0 }))
            .filter((s) => s.count > 0);

          return (
            <animated.div
              key={t}
              style={style}
              className="grid grid-cols-[minmax(120px,200px)_1fr_44px] gap-3 items-center"
            >
              <button
                type="button"
                onClick={() => onRowClick?.(t)}
                className={cn(
                  "text-xs font-medium text-foreground/80 truncate text-left rounded px-2 py-1 hover:bg-muted transition-colors cursor-pointer",
                  activeType === t && activeBucket === "all" && "ring-1 ring-foreground bg-muted",
                )}
                title={`Drill into all ${t} findings`}
              >
                {t}
              </button>

              <div className="relative h-6">
                {total === 0 ? (
                  <div className="absolute inset-y-0 left-0 h-full w-full rounded bg-muted" />
                ) : (
                  <div
                    className="absolute inset-y-0 left-0 flex h-full gap-[2px] overflow-hidden rounded"
                    style={{ width: `${barPct}%` }}
                  >
                    {segments.map((s) => {
                      const isActive = activeBucket === s.bucket && activeType === t;
                      return (
                        <button
                          key={s.bucket}
                          type="button"
                          onClick={() => onCellClick?.(s.bucket, t)}
                          className={cn(
                            "h-full min-w-[4px] transition-all hover:brightness-110",
                            SEGMENT_COLOR[s.bucket],
                            isActive && "ring-2 ring-offset-1 ring-foreground",
                          )}
                          style={{ flexGrow: s.count, flexBasis: 0 }}
                          title={`${t} · ${s.bucket}: ${s.count} finding${s.count === 1 ? "" : "s"}`}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="text-xs font-mono text-right text-muted-foreground" title={`${t}: ${total} total`}>
                {total}
              </div>
            </animated.div>
          );
        })}
      </div>
    </div>
  );
}

export default ImpactSeverityRanked;
